import { useState, useMemo, useEffect, useRef} from 'react';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { RemoveSavedFlyerModal } from './components/RemoveSavedFlyerModal';
import InfiniteScroll from 'react-infinite-scroll-component';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useNavigate } from 'react-router-dom';
import {
  LoadingSpinner,
  EmptyState,
  FavoriteIcon,
  Divider,
  AccessibleDivButton,
  LiveFeedback,
} from '@/components';
import {
  NavBarDefault,
  TitleSmallEmphasized,
  useSnackbar,
  Tag,
  ButtonFlatMediumEmphasized,
} from '@/design-system';
import { APP_ROUTES, DEFAULT_ERROR_MESSAGE, PAGE_SIZE } from '@/config/constants';
import { AllFlyerFilters, FlyerCardThumbnail, GetAllFlyersResponse, OwningTypeEnum, UnsaveFlyerResponse } from '@/graphql/types';
import { GET_ALL_FLYERS } from '@/graphql/queries';
import {
  districtVar,
} from '@/graphql/state';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import styles from './Saved.styles'
import colors from '@/theme/colors';
import { useAnalytics, useIsVisible } from '@/hooks';
import { SAVE_FLYER_MUTATION, UNSAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { SortDrawer } from './components/SortDrawer';
import { Card } from './components/Card';
import { FlyerStatus } from '@/types/flyer';

const DEFAULT_SORT_VALUE = 'owningType';

export const Saved: React.FC = () => {
  const { track } = useAnalytics();
  const trackId = 'saved';
  const ref = useRef<HTMLDivElement | null>(null);
  const isPageVisible = useIsVisible(ref);
  const navigate = useNavigate();
  const showToast = useSnackbar();
  const [unsaveFlyers,] = useMutation<UnsaveFlyerResponse>(UNSAVE_FLYER_MUTATION);
  const [loading, setLoading] = useState<boolean>(false);
  const [flyers, setFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [expiredFlyers, setExpiredFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [removedFlyers, setRemovedFlyers] = useState<FlyerCardThumbnail[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [totalExpired, setTotalExpired] = useState<number>(0);
  const [totalRemoved, setTotalRemoved] = useState<number>(0);
  const [unsaveFlyerId, setUnsaveFlyerId] = useState<number>(0);
  const [showExpired, setShowExpired] = useState<boolean>(false);
  const [showRemoved, setShowRemoved] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedSortValue, setSelectedSortValue] = useState<string>(DEFAULT_SORT_VALUE);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [liveRegionMessage, setLiveRegionMessage] = useState<string>('');
  const nextFocusRef = useRef<HTMLElement | null>(null);

  const anchorRef = useRef<HTMLDivElement>(null);

  const district = useReactiveVar(districtVar);
  const [getAllFlyers] = useLazyQuery<GetAllFlyersResponse>(GET_ALL_FLYERS)

  const pages = useMemo(() => {
    let totalFlyers = total;
    if(showRemoved) {
      totalFlyers = totalRemoved;
    } else if(showExpired) {
      totalFlyers = totalExpired;
    }

    return Math.ceil(total / PAGE_SIZE);
  },
  [total, showExpired, totalExpired]);

  const resetPaginationStates = () => {
    setTotal(0);
    setTotalExpired(0);
    setTotalRemoved(0);
    setFlyers([]);
    setExpiredFlyers([]);
    setRemovedFlyers([]);
    setShowExpired(false)
    setShowRemoved(false);
    setPage(1);
    setTriggerFetch(prev => !prev);
  }

  useEffect(() => {
    if(!isPageVisible) return;
    // change the sort option
    resetPaginationStates();
  }, [selectedSortValue, isPageVisible]);

  useEffect(() => {
    if (!district || !isPageVisible) return;
    // check if should show the expired or removed saved flyers
    if (
      page === pages &&
      !showExpired) {
     setShowExpired(true);
    }
    else if(
      page === pages &&
      !showRemoved){
        setShowRemoved(true);
    }

      fetchData();
  }, [page, district, triggerFetch]);

  useEffect(() => {
    setPage(1)
  }, [showExpired])

  const fetchData =  async() => {
    if((pages > 0 && page > pages) || !district) {
      return;
    }

    let filters: AllFlyerFilters = {
      allDistrictSchools: true,
      schoolIds: [],
      districtId: Number(district.districtId),
      isExpired: showExpired && !showRemoved,
      isRemoved: showRemoved,
      isFavorite: true,
    }

    try{
      setLoading(true);
      const { data } = await getAllFlyers({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            filters,
            pagination: {
              pageNumber: page,
              pageSize: PAGE_SIZE
            },
            sort: {
              sortBy: selectedSortValue,
            }
          }
        }
      });

      const { items, pagination } = data?.getAllFlyers || { items: [], pagination: {} };
      const paginationTotal = pagination?.total || 0;

      if(items && items?.length > 0) {
        if(showRemoved) {
          setRemovedFlyers((prevData) => [...prevData, ...items])
        } else if(showExpired) {
          setExpiredFlyers((prevData) => [...prevData, ...items])
        }
        else {
          setFlyers((prevData) => [...prevData, ...items]);
        }
      }

      if(paginationTotal > 0) {
        if(showRemoved) {
          setTotalRemoved(paginationTotal);
        } else if(showExpired) {
          setTotalExpired(paginationTotal);
        }
        else {
          setTotal(paginationTotal);
        }
      }

      if (items && (
        items.length === 0 || (page === 1 && items.length < PAGE_SIZE)
      ) && !showExpired) {
        setShowExpired(true);
        setTriggerFetch(prev => !prev);
        return;
      }

      if (items && (
        items.length === 0 || (page === 1 && items.length < PAGE_SIZE)
      ) && !showRemoved) {
        setShowRemoved(true);
        setTriggerFetch(prev => !prev);
        return;
      }

    }catch(err) {
      console.log(err);
    }
    finally {
      setLoading(false)
    }
  }


  const nextPage = () => {
    setPage((prevPage) => prevPage + 1)
  }

  const onCancelRemoveSavedFlyer = () => {
    setUnsaveFlyerId(0);
  }

  const onClickUnsaveFlyers = async({ isExpired } : { isExpired: boolean }) => {
    const flyersList = isExpired ? expiredFlyers : removedFlyers;
    const flyerIds = flyersList.map(flyer => flyer.flyerId);
    await onUnSaveFlyers(flyerIds);
  }

  const onUnSaveFlyers = async(flyerIds: number[]) => {
    try {
      const { data } = await unsaveFlyers({
        variables: {
          input: {
            flyerIds: flyerIds,
          },
        },
      });
      if(data?.unsaveFlyers.success) {
        const {
          newFlyers,
          newExpiredFlyers,
          newRemovedFlyers,
          removedCount,
          removedExpiredCount,
          removedRemovedCount,
        } = flyerIds.reduce((acc, flyerId) => {
          const isFlyerExists = flyers.some(flyer => flyer.flyerId === flyerId);
          const isExpiredFlyerExists = !isFlyerExists && expiredFlyers.some(flyer => flyer.flyerId === flyerId);
          const isRemovedFlyerExists = !isExpiredFlyerExists && removedFlyers.some(flyer => flyer.flyerId === flyerId);
          if (isFlyerExists) {
            acc.removedCount += 1;
            acc.newFlyers = acc.newFlyers.filter(flyer => flyer.flyerId !== flyerId);
          } else if (isExpiredFlyerExists) {
            acc.removedExpiredCount += 1;
            acc.newExpiredFlyers = acc.newExpiredFlyers.filter(flyer => flyer.flyerId !== flyerId);
          } else if (isRemovedFlyerExists) {
            acc.removedRemovedCount += 1;
            acc.newRemovedFlyers = acc.newRemovedFlyers.filter(flyer => flyer.flyerId !== flyerId);
          }

          return acc;
        }, {
            newFlyers: [...flyers],
            newExpiredFlyers: [...expiredFlyers],
            newRemovedFlyers: [...removedFlyers],
            removedCount: 0,
            removedExpiredCount: 0,
            removedRemovedCount: 0,
        });

        if (removedCount > 0) {
          setTotal(prevTotal => prevTotal - removedCount);
          setFlyers(newFlyers);
          setLiveRegionMessage(`${removedCount} flyer(s) removed from saved list.`);

        }

        if (removedExpiredCount > 0) {
          setTotalExpired(prevTotal => prevTotal - removedExpiredCount);
          setExpiredFlyers(newExpiredFlyers);
          setLiveRegionMessage(`${removedExpiredCount} expired flyer(s) removed.`);
        }

        if (removedRemovedCount > 0) {
          setTotalRemoved(prevTotal => prevTotal - removedExpiredCount);
          setRemovedFlyers(newRemovedFlyers);
          setLiveRegionMessage(`${removedRemovedCount} removed flyer(s) deleted.`);
        }


        setTimeout(() => {
          if (nextFocusRef.current) {
            nextFocusRef.current.focus();
          } else {
            setFocusGoExploreButton();
          }
        }, 500)
      }
    }catch(err) {
      showToast(DEFAULT_ERROR_MESSAGE);
    }
  }

  const onRemoveSavedFlyer = async() => {
    const flyerIds = [unsaveFlyerId];
    await onUnSaveFlyers(flyerIds);
    onCancelRemoveSavedFlyer();
  }

  const setFocusGoExploreButton = () => {
    document.querySelector<HTMLElement>('#go-explore-button')?.focus();
  }

  const findNextFocusableElement = (currentElement: HTMLElement, container: HTMLElement): HTMLElement | null => {
    const focusableSelectors = [
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const focusableElements = Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors.join(',')));
    focusableElements.sort((a, b) => {
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    const currentIndex = focusableElements.indexOf(currentElement);

    if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
      return focusableElements[currentIndex + 1];
    }
    if (currentIndex >= 3 && currentIndex <= focusableElements.length -1)  {
      return focusableElements[currentIndex - 3];
    }

    return null;
  }


  const onOpenRemovalModal = (flyerId: number) => {
    setUnsaveFlyerId(flyerId);
    const currentElement = document.activeElement as HTMLElement;
    const mainElement = document.querySelector<HTMLElement>('main');
    if (currentElement && mainElement) {
      const nextElement = findNextFocusableElement(currentElement, mainElement);
      nextFocusRef.current = nextElement;
    }
  }


  const renderGoExploreLink = () => (
      <ButtonFlatMediumEmphasized onClick={() => navigate(APP_ROUTES.explore)} id="go-explore-button" autoFocus>
          Go Explore
      </ButtonFlatMediumEmphasized>
  );

  const clearAllButton = ({ isExpired }: { isExpired: boolean }) => (
      <ButtonFlatMediumEmphasized onClick={async() => await onClickUnsaveFlyers({ isExpired })}>
        Clear All
      </ButtonFlatMediumEmphasized>
  );

  const renderInfiniteScroll = (
    { flyers, total, isExpired = false, isRemoved = false }
    : { flyers: FlyerCardThumbnail[], total: number, isExpired?: boolean; isRemoved?: boolean }
  ) => {
    const flyersTotal = flyers.length;
    const hasMore = flyersTotal < total;

    return (
      <InfiniteScroll
        dataLength={flyersTotal}
        next={nextPage}
        hasMore={hasMore}
        loader={<LoadingSpinner />}
      >
      {flyers.map(
          (flyer) => (
            <Card
              key={flyer.flyerId}
              flyer={flyer}
              trackId={trackId}
              isExpired={isExpired}
              isRemoved={isRemoved}
              unsaveFlyer={onOpenRemovalModal}
            />
          ),
        )}
      </InfiniteScroll>
    )
  };

  const currentProgramList = useMemo(() =>
    flyers.length > 0 && renderInfiniteScroll({ flyers, total }),
  [flyers]);

  const expiredFlyersList = useMemo(() => (
    showExpired && expiredFlyers.length > 0 && (
      <>
        <Divider className={styles.dividerSpacing} />
        <section className={styles.section}>
          <div className={styles.rowContainer}>
            <TitleSmallEmphasized>
              Expired Flyers
            </TitleSmallEmphasized>
            {clearAllButton({ isExpired: true })}
          </div>
          </section>
          {renderInfiniteScroll({
            flyers: expiredFlyers,
            total: totalExpired,
            isExpired: true
          })}
      </>
    )
  ), [showExpired, expiredFlyers]);


  const removedFlyersList = useMemo(() => (
    showRemoved && removedFlyers.length > 0 && (
      <>
        <Divider className={styles.dividerSpacing} />
        <section className={styles.section}>
          <div className={styles.rowContainer}>
            <TitleSmallEmphasized>
              Removed Flyers
            </TitleSmallEmphasized>
            {clearAllButton({ isExpired: false })}
          </div>
        </section>
        {renderInfiniteScroll({
          flyers: removedFlyers,
          total: totalRemoved,
          isRemoved: true,
        })}
      </>
    )
  ), [showRemoved, removedFlyers]);

  const handleSortChange = (value: string) => {
    setSelectedSortValue(value);
    handleCloseDrawer();
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    anchorRef.current?.focus();
  }

  return (
    <div ref={ref}>
      <LiveFeedback aria-live="polite" aria-atomic="true" message={liveRegionMessage} />
      <RemoveSavedFlyerModal
        isOpen={!!unsaveFlyerId}
        onConfirm={onRemoveSavedFlyer}
        onCancel={onCancelRemoveSavedFlyer}
      />
      <NavBarDefault>
        Saved
      </NavBarDefault>
      <main>
        <div className={styles.container}>
          <div className={styles.content}>
            <section className={styles.section}>
              <div className={styles.rowContainer}>
                <TitleSmallEmphasized>
                  Current Program
                </TitleSmallEmphasized>
                <AccessibleDivButton
                  onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  ref={anchorRef}
                >
                  <Tag
                    variant='outline'
                    rightIcon={<SwapVertIcon sx={{ fontSize: '15px' }} />}

                  >
                    Sort
                  </Tag>
                </AccessibleDivButton>
              </div>

              {loading && flyers.length === 0 && expiredFlyers.length === 0 &&
              removedFlyers.length === 0 && (
                <LoadingSpinner />
              )}

              {!loading && flyers.length === 0 && (
                <EmptyState
                  title="You currently do not have any saved flyers"
                  titleColor={colors.slate}
                  description={
                    <span>
                      Don't let the good flyers fly away! Search and browse for
                      flyers of interest, then click the start icon (
                      <span className={styles.iconEmptyState}>
                        <FavoriteIcon isFavorite={false} size={24} />
                      </span>
                      ) to save them and easily find then again here in the future.
                    </span>
                  }
                  bottom={renderGoExploreLink()}
                />
              )}
            </section>
            {currentProgramList}
            {expiredFlyersList}
            {removedFlyersList}
          </div>
        </div>
      </main>
      <SortDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        onChange={handleSortChange}
        value={selectedSortValue}
        anchorRef={anchorRef}
      />
    </div>
  )
};
